import React, { FC, useEffect, useRef, useState } from 'react';
import { getTranslatedSummary } from 'app/functions/product';
import { ProductSummary } from 'app/containers/ProductSummary';
import { ProductsId } from '@webc/meeseeks-ui-sdk';
import * as styles from './ProductPopup.scss';
import { useTranslations } from 'app/hooks/useTranslations';
import { PartWrapper } from 'app/containers/PartWrapper/PartWrapper';
import { a11yLabelsKeys } from 'app/providers/a11yLabels';
import { isSearchInvalid } from 'app/functions/general';
import { useAppSelector } from 'app/hooks/appHooks';
import {
  autoUpdateSelector,
  groupsTripsMessageSelector,
  isNecCardSelector,
  passengerCountSelector,
} from 'app/store/selectors';
import { ProductDataType } from 'app/models/types/store/product';
import { breakpointMatches } from 'app/functions';
import { ProductPopupContent } from 'app/components/product/ProductPopupContent/ProductPopupContent';
import { useOutsideClick } from 'app/hooks/useClickOutside';
import { emitFieldClosedEvent, emitFieldOpenedEvent } from 'app/events';
import { FIELD_PRODUCTS } from 'app/storage/fieldNames';
import { closeElement } from 'app/functions/general/closeElement';
import { StoreUiField } from 'app/models/types/store/ui';
import { toggleOverlay } from 'app/store/reducers/ui';
import { requestSearch } from 'app/store/thunk/general/requestSearchThunk';
import { useAppDispatch } from 'app/hooks/appHooks';
import { UpdateProductQuantityType } from 'app/store/thunk/product/updateProductQuantityThunk';

export type ProductPopupProps = {
  label: string;
  selectedProducts: { [key in ProductsId]?: ProductDataType };
  extendedProductsInformationLink: string;
  onSetProductQuantity: ({ productType, quantity }: UpdateProductQuantityType) => void;
  popupTitle: string;
  focusedPart: string | null;
  onChangeFocusedPart: (focusedPart: StoreUiField['focusedPart']) => null;
  showExtendedProductsInfo: boolean;
};

export const ProductPopup: FC<ProductPopupProps> = ({
  selectedProducts,
  extendedProductsInformationLink,
  onSetProductQuantity,
  label,
  focusedPart,
  onChangeFocusedPart,
  showExtendedProductsInfo,
}) => {
  const [infoMessagesSeen, setInfoMessagesSeen] = useState<Record<string, boolean>>({});
  const [isChanged, setIsChanged] = useState(false);
  const [isOpen, setOpen] = useState(false);

  const wrapperRef = useRef(null);
  const input = useRef<HTMLInputElement>();

  const isSmallBreakpoint = breakpointMatches('small').matches;

  const passengerCount = useAppSelector(passengerCountSelector);

  const { getInputAriaLabel } = useTranslations({
    getInputAriaLabel: a11yLabelsKeys.products.input,
  });
  const isGroupTripsMessageAvailable = useAppSelector(groupsTripsMessageSelector);
  const isAutoUpdate = useAppSelector(autoUpdateSelector);

  const dispatch = useAppDispatch();

  const { getTranslation } = useTranslations({}, true);

  const toggleInfoMessageSeen = (messageKey: string, value: boolean) => {
    setInfoMessagesSeen((prevState) => ({
      ...prevState,
      [messageKey]: value,
    }));
  };

  const handleSetProductQuantity = (productType: ProductsId, quantity: number) => {
    setIsChanged(true);
    onSetProductQuantity({ productType, quantity });
  };

  useEffect(() => {
    if (focusedPart !== 'product' && isOpen) {
      handleClose(true);
    }
    if (isOpen && focusedPart === 'product') {
      input.current?.focus();
    }
  }, [focusedPart, isOpen]);

  const handleClose = (isOutsideClick?: boolean) => {
    if (isAutoUpdate && isChanged) {
      dispatch(requestSearch({}));
      setIsChanged(false);
    }
    setOpen(false);
    if (!isOutsideClick) {
      input.current?.focus();
    }
    if (isSmallBreakpoint) {
      emitFieldClosedEvent(FIELD_PRODUCTS, { selectedProducts: Object.keys(selectedProducts) });
    } else {
      closeElement(FIELD_PRODUCTS, { selectedProducts: Object.keys(selectedProducts) });
    }
  };

  const isValid = !isSearchInvalid({ passengerCount });

  const ariaLabel = isValid
    ? `${getInputAriaLabel()}, ${getTranslatedSummary(selectedProducts, getTranslation)}`
    : getInputAriaLabel();

  const productContentProps = {
    toggleInfoMessageSeen,
    extendedProductsInformationLink,
    infoMessagesSeen,
    showExtendedProductsInfo,
    isGroupTripsMessageAvailable,
    wrapperRef,
    handleClose,
    handleSetProductQuantity,
  };

  const handleOpen = () => {
    if (focusedPart !== 'product') {
      onChangeFocusedPart('product');
    }
    if (isSmallBreakpoint) {
      dispatch(toggleOverlay({ name: 'ProductPanel', props: productContentProps }));
    } else {
      setOpen(true);
    }
    emitFieldOpenedEvent(FIELD_PRODUCTS);
  };

  useOutsideClick(wrapperRef, () => {
    if (isOpen) {
      handleClose(true);
    }
  });

  const isNecCard = useAppSelector(isNecCardSelector);
  const adjustedLabel = `${label}${isNecCard ? ' / NEC' : ''} `;

  return (
    <div ref={wrapperRef}>
      <PartWrapper extraClasses={styles.productWrapper} onClose={handleClose}>
        <ProductSummary
          value={getTranslatedSummary(selectedProducts, getTranslation)}
          onClick={isOpen ? handleClose : handleOpen}
          label={adjustedLabel}
          // @ts-expect-error ignore
          inputRef={(el: HTMLInputElement) => {
            input.current = el;
          }}
          isExpanded={isOpen}
        />
        {isOpen && (
          <div
            aria-label={ariaLabel}
            id="dialog-products"
            aria-hidden={!isOpen}
            className={styles.productPopupWrapper}
            role="dialog"
          >
            <ProductPopupContent {...productContentProps} />
          </div>
        )}
      </PartWrapper>
    </div>
  );
};
